import axios from 'axios';
import TokenService from './tokenService';

const apiClient = axios.create();
const publicPaths = ['/auth/admin_signin', '/auth/verify', '/auth/refresh'];

let baseUrlCache = '';
let cleanEndpoint = '';
let cleanBase = '';
let refreshPromise = null;

async function initApiClient() {
  try {
    const { apiEndpoint = '', apiBaseUrl = '' } = (await axios.get('/config.json')).data;
    cleanEndpoint = apiEndpoint.replace(/\/+$/, '');
    cleanBase = apiBaseUrl.replace(/\/+$/, '');
    baseUrlCache = `${cleanEndpoint}${cleanBase}`;
    apiClient.defaults.baseURL = baseUrlCache;
  } catch (err) {
    console.error('Error fetching config:', err);
  }
}
initApiClient();

export const refreshTokens = async () => {
  if (refreshPromise) return refreshPromise;

  try {
    refreshPromise = new Promise(async (resolve, reject) => {
      const refreshToken = TokenService.getRefreshToken();
      if (!refreshToken || refreshToken.trim() === '') {
        TokenService.clearTokens();
        reject(new Error('No refresh token'));
        return;
      }

      try {
        const response = await axios.post(
          `${cleanEndpoint}/auth/refresh`,
          {},
          { headers: { 'X-Refresh-Token': refreshToken } }
        );

        TokenService.setAccessToken(response.data.AccessToken);
        TokenService.setIdToken(response.data.IdToken);
        TokenService.setRefreshToken(response.data.RefreshToken || refreshToken);
        apiClient.defaults.headers.common.Authorization = `Bearer ${response.data.AccessToken}`;
        
        resolve(response.data);
      } catch (error) {
        TokenService.clearTokens();
        reject(error);
      } finally {
        refreshPromise = null;
      }
    });

    return await refreshPromise;
  } catch (error) {
    refreshPromise = null;
    throw error;
  }
};

apiClient.interceptors.request.use(
  config => {
    config.isPublic = publicPaths.includes(config.url);
    
    if (config.url === '/auth/refresh') {
      const refreshToken = TokenService.getRefreshToken();
      if (refreshToken) {
        config.headers['X-Refresh-Token'] = refreshToken;
      }
      config.baseURL = cleanEndpoint;
      return config;
    }

    if (config.url === '/impersonate') {
      config.isImpersonate = true; 
      const accessToken  = TokenService.getAccessToken();
      const refreshToken = TokenService.getRefreshToken();
      if (refreshToken) {
        config.headers['X-Refresh-Token'] = refreshToken;
      }
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
      return config;
    }
    
    if (config.url === '/signout') {
      const token = TokenService.getAccessToken();
      const refreshToken = TokenService.getRefreshToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      if (refreshToken) {
        config.headers['X-Refresh-Token'] = `${refreshToken}`;
      }
      return config;
    }

    if (config.isPublic) {
      config.baseURL = cleanEndpoint;
      delete config.headers.Authorization;
      return config;
    }

    const token = TokenService.getAccessToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  error => Promise.reject(error)
);

apiClient.interceptors.response.use(
  response => {
    if (response.config.isImpersonate && response.data) {
      const { AccessToken, IdToken, RefreshToken } = response.data;
      
      TokenService.setAccessToken(AccessToken);
      TokenService.setIdToken(IdToken);
      if (RefreshToken) {
        TokenService.setRefreshToken(RefreshToken);
      }
      
      apiClient.defaults.headers.common.Authorization = `Bearer ${AccessToken}`;
    }
    return response;
  },
  async error => {
    const originalRequest = error.config;
    const status = error.response?.status;

    if (originalRequest.url === '/signout') {
      TokenService.clearTokens();
      return Promise.reject(error);
    }

    if (originalRequest.isPublic) {
      return Promise.reject(error);
    }

    if (status === 401) {
      TokenService.clearTokens();
      const isPublicPage = ['/', '/otp'].includes(window.location.pathname);
      if (!isPublicPage) {
        window.location.href = '/';
      }
      return Promise.reject(error);
    }

    if (status === 403 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        await refreshTokens();
        const newToken = TokenService.getAccessToken();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        TokenService.clearTokens();
        window.location.href = '/';
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

export default apiClient;